import React, { useState } from "react";
import DateRangeModal from "../leads/DateRangeModal";

const AGENT_COLUMN_DEFINITIONS = [
  { col: "Calls Attempted",  def: "Total number of call attempts made by this agent in the period, including missed, dropped, and connected calls." },
  { col: "Connected Calls",  def: "Calls that were answered and lasted longer than the minimum duration threshold (default 10 seconds)." },
  { col: "Active Time / Day",def: "Average total time the agent spent on calls per calendar day in the selected period." },
  { col: "Unique Attempts",  def: "Number of distinct leads this agent dialled at least once. Multiple calls to the same lead count as one." },
  { col: "Unique Connected", def: "Number of distinct leads the agent actually spoke with (had at least one connected call)." },
  { col: "Contact Rate",     def: "Unique Connected ÷ Unique Attempts — shows how effectively the agent is reaching the leads they dial." },
  { col: "Assigned Leads",   def: "Total leads currently assigned to this agent." },
  { col: "Conversions",      def: "Leads in Booked or Delivered status that are assigned to this agent." },
  { col: "Talk Time",        def: "Average duration of connected calls (calls that were answered). Excludes unanswered attempts." },
];

function AgentColumnInfoModal({ onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel col-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-panel-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ paddingLeft: "0.5rem" }}>
            <h3>Agent Wise Report: Column Guide</h3>
            <p style={{ fontSize: "0.8rem", color: "#99a5ba", margin: 0, paddingLeft: "0.25rem" }}>What each metric means</p>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>✕</button>
        </div>
        <div className="col-info-modal-body">
          {AGENT_COLUMN_DEFINITIONS.map(({ col, def }) => (
            <div key={col} className="col-info-row">
              <span className="col-info-name">{col}</span>
              <span className="col-info-def">{def}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InfoIconButton({ onClick }) {
  return (
    <button type="button" className="col-info-trigger" onClick={onClick} title="Column guide" aria-label="What do these columns mean?">
      <svg viewBox="0 0 16 16" fill="currentColor" width="15" height="15">
        <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Z"/>
        <path d="M7.25 6.75A.75.75 0 0 1 8 6h.01a.75.75 0 0 1 0 1.5H8a.75.75 0 0 1-.75-.75ZM6.5 10.25a.75.75 0 0 1 .75-.75h.75V8.5H7.25a.75.75 0 0 1 0-1.5H8.5a.75.75 0 0 1 .75.75v2h.25a.75.75 0 0 1 0 1.5h-2.25a.75.75 0 0 1-.75-.75Z"/>
      </svg>
    </button>
  );
}

import {
  BarsMiniIcon,
  BoltMiniIcon,
  CalendarIcon,
  DocumentIcon,
  GlobeIcon,
  LocationIcon,
  UserGroupIcon,
} from "../../lib/icons";
import { PERIOD_OPTIONS, REPORT_TABS } from "../../lib/constants";
import {
  formatCompactNumber,
  formatMonthLabel,
  formatRate,
  riskTone,
  startCase,
  toneForSource,
} from "../../lib/utils";

function formatShortDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

function AnalyticsSummaryCard({ title, value }) {
  return (
    <article className="analytics-summary-card">
      <span>{title}</span>
      <strong>{value}</strong>
    </article>
  );
}

function FunnelStageRow({ stage, isFirst, maxValue }) {
  return (
    <div className="funnel-stage-row">
      <div className="funnel-stage-label">{stage.label}</div>
      <div className="funnel-stage-main">
        <div className="funnel-stage-bar-wrap">
          <div
            className={`funnel-stage-bar tone-${stage.tone}`}
            style={{ width: `${Math.max((stage.value / maxValue) * 100, stage.value ? 11 : 0)}%` }}
          >
            <span>{formatCompactNumber(stage.value)}</span>
          </div>
        </div>
        <div className="funnel-stage-side">
          <strong>{isFirst ? "100%" : `${formatRate(stage.rate)}%`}</strong>
          {!isFirst ? <span>↓{formatRate(stage.drop_rate)}% drop</span> : <span>&nbsp;</span>}
        </div>
      </div>
      {!isFirst ? (
        <div className="funnel-stage-drop-copy">
          -{formatCompactNumber(stage.drop_count)} ({formatRate(stage.drop_rate)}% drop)
        </div>
      ) : (
        <div className="funnel-stage-drop-copy">—</div>
      )}
    </div>
  );
}

function SourceBarRow({ item, maxValue }) {
  return (
    <div className="source-bar-row">
      <div className="source-bar-topline">
        <span>{item.source_name}</span>
        <strong>{formatCompactNumber(item.total)}</strong>
      </div>
      <div className="source-bar-track">
        <span
          className={`source-bar-fill tone-${toneForSource(item.source_name, item.source_type)}`}
          style={{ width: `${Math.max((item.total / maxValue) * 100, item.total ? 8 : 0)}%` }}
        />
      </div>
    </div>
  );
}

function iconForReportTab(label) {
  if (label === "Funnel") return <BoltMiniIcon />;
  if (label === "City Wise") return <LocationIcon />;
  if (label === "Agent Wise") return <UserGroupIcon />;
  if (label === "Region Wise") return <GlobeIcon />;
  if (label === "Status Wise") return <BarsMiniIcon />;
  return <DocumentIcon />;
}

function ReportsView({
  activeReportTab,
  agents,
  context,
  exporting,
  loading,
  onDownloadReport,
  onReportDraftChange,
  onReportFilterReset,
  reportData,
  reportDraftFilters,
  setActiveReportTab,
  roleScope,
}) {
  const [agentInfoOpen, setAgentInfoOpen] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const dateLabel = reportDraftFilters.dateFrom || reportDraftFilters.dateTo
    ? `Filter by Date: ${formatShortDate(reportDraftFilters.dateFrom) || "…"} – ${formatShortDate(reportDraftFilters.dateTo) || "…"}`
    : "Filter by Date";
  const funnelStages = reportData?.stages || [];
  const sourceBreakdown = reportData?.source_breakdown || [];
  const agentBreakdown = reportData?.agent_breakdown || [];
  const cityBreakdown = reportData?.city_breakdown || [];
  const regionBreakdown = reportData?.region_breakdown || [];
  const maxSourceTotal = Math.max(...sourceBreakdown.map((item) => item.total), 1);
  const reportScope = roleScope?.report_scope || "client";
  const ZONAL_TABS = ["City Wise", "Region Wise", "Status Wise", "Source Wise"];
  const CLIENT_TABS = ["Status Wise"];
  const visibleTabs = reportScope === "all"
    ? REPORT_TABS
    : reportScope === "zonal"
      ? REPORT_TABS.filter((tab) => ZONAL_TABS.includes(tab))
      : REPORT_TABS.filter((tab) => CLIENT_TABS.includes(tab));
  const exportDisabled = loading || exporting || !reportData;
  const stageAnalysis = funnelStages.slice(0, -1).map((stage, index) => {
    const nextStage = funnelStages[index + 1];
    const advanced = nextStage?.value ?? 0;
    const advanceRate = stage.value > 0 ? Math.round((advanced / stage.value) * 1000) / 10 : 0;
    return {
      from: stage.label.replace(" Leads", ""),
      to: nextStage?.label.replace(" Leads", "") ?? "",
      leads: stage.value,
      advanced,
      advanceRate,
    };
  });

  return (
    <section className="analytics-shell">
      <section className="toolbar-panel reports-toolbar-panel">
        <div className="report-nav-row">
          <div className="report-tab-strip">
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                className={`report-tab-button ${activeReportTab === tab ? "active" : ""}`}
                onClick={() => setActiveReportTab(tab)}
              >
                {iconForReportTab(tab)}
                <span>{tab}</span>
              </button>
            ))}
          </div>

          <div className="report-action-group">
            <button
              type="button"
              className="secondary-ghost compact-button"
              disabled={exportDisabled}
              onClick={() => onDownloadReport("csv")}
            >
              <DocumentIcon />
              <span>{exporting ? "Exporting..." : "CSV"}</span>
            </button>
            <button
              type="button"
              className="filter-button compact-button"
              disabled={exportDisabled}
              onClick={() => onDownloadReport("xlsx")}
            >
              <DocumentIcon />
              <span>{exporting ? "Exporting..." : "XLSX"}</span>
            </button>
          </div>
        </div>

        <div className="report-filter-row">
          <span className="quick-label">Filters:</span>

          <select
            value={reportDraftFilters.sourceId}
            onChange={(event) => onReportDraftChange("sourceId", event.target.value)}
          >
            <option value="">All Sources</option>
            {context.lead_sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>

          <select
            value={reportDraftFilters.period}
            onChange={(event) => onReportDraftChange("period", event.target.value)}
          >
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="filter-toolbar-button compact-button date-filter-button"
            onClick={() => setDateModalOpen(true)}
          >
            <CalendarIcon />
            <span>{dateLabel}</span>
          </button>

          <select
            value={reportDraftFilters.region}
            onChange={(event) => onReportDraftChange("region", event.target.value)}
          >
            <option value="">All Regions</option>
            {(reportData?.available_regions || []).map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>

          {roleScope?.scope !== "own_leads" ? (
            <select
              value={reportDraftFilters.assignedToId}
              onChange={(event) => onReportDraftChange("assignedToId", event.target.value)}
            >
              <option value="">All Agents</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.full_name}
                </option>
              ))}
            </select>
          ) : null}

          <button type="button" className="secondary-ghost compact-button" onClick={onReportFilterReset}>
            Reset
          </button>
        </div>
      </section>

      {loading ? <section className="report-state-card">Loading report data...</section> : null}
      {!loading && !reportData ? (
        <section className="report-state-card">
          Report data is unavailable right now. Adjust filters or retry.
        </section>
      ) : null}

      <section className="analytics-summary-grid reports-summary-grid">
        <AnalyticsSummaryCard
          title="Total Leads"
          value={formatCompactNumber(reportData?.summary?.total_leads || 0)}
        />
        <AnalyticsSummaryCard
          title="Attempted"
          value={formatCompactNumber(reportData?.summary?.attempted || 0)}
        />
        <AnalyticsSummaryCard
          title="Contacted"
          value={formatCompactNumber(reportData?.summary?.contacted || 0)}
        />
        <AnalyticsSummaryCard
          title="Test Drive Sched."
          value={formatCompactNumber(reportData?.summary?.test_drive_scheduled || 0)}
        />
        <AnalyticsSummaryCard
          title="Booked"
          value={formatCompactNumber(reportData?.summary?.booked || 0)}
        />
        <AnalyticsSummaryCard
          title="Conv. Rate"
          value={`${formatRate(reportData?.summary?.conversion_rate || 0)}%`}
        />
      </section>

      {activeReportTab === "Funnel" ? (
        <>
          <section className="report-card">
            <div className="report-card-header">
              <div>
                <h2>Lead Conversion Funnel</h2>
                <p>{formatMonthLabel(new Date())} Performance</p>
              </div>
            </div>

            <div className="funnel-stage-list">
              {funnelStages.map((stage, index) => (
                <FunnelStageRow
                  key={stage.key}
                  stage={stage}
                  isFirst={index === 0}
                  maxValue={funnelStages[0]?.value || 1}
                />
              ))}
            </div>
          </section>

          <section className="analytics-two-column">
            <article className="report-card">
              <div className="report-card-header">
                <div>
                  <h2>Stage Progression</h2>
                  <p>Leads advancing to each next stage</p>
                </div>
              </div>

              <div className="table-scroll report-table-scroll">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>To</th>
                      <th>At Stage</th>
                      <th>Advanced</th>
                      <th>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stageAnalysis.map((item) => (
                      <tr key={item.from}>
                        <td>{item.from}</td>
                        <td>{item.to}</td>
                        <td className="mono-text">{formatCompactNumber(item.leads)}</td>
                        <td className="mono-text">{formatCompactNumber(item.advanced)}</td>
                        <td>{formatRate(item.advanceRate)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="report-card">
              <div className="report-card-header">
                <div>
                  <h2>Funnel by Source</h2>
                </div>
              </div>

              <div className="table-scroll report-table-scroll">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Total</th>
                      <th>Contacted</th>
                      <th>Booked</th>
                      <th>Conv%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceBreakdown.map((item) => (
                      <tr key={item.source_id}>
                        <td>
                          <span className="source-dot-wrap">
                            <span className={`source-dot tone-${toneForSource(item.source_name, item.source_type)}`} />
                            {item.source_name}
                          </span>
                        </td>
                        <td className="mono-text">{formatCompactNumber(item.total)}</td>
                        <td>{formatCompactNumber(item.contacted)}</td>
                        <td>{formatCompactNumber(item.booked)}</td>
                        <td className="success-rate">{formatRate(item.conversion_rate)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          </section>
        </>
      ) : null}

      {activeReportTab === "Agent Wise" ? (
        <section className="report-card">
          <div className="report-card-header">
            <div>
              <h2>
                Agent Wise Report
                <InfoIconButton onClick={() => setAgentInfoOpen(true)} />
              </h2>
              <p>Call activity, unique lead coverage, and conversion performance.</p>
            </div>
          </div>
          <div className="table-scroll report-table-scroll">
            <table className="report-table agent-wise-table">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Calls Attempted</th>
                  <th>Connected Calls</th>
                  <th>Active Time / Day</th>
                  <th>Unique Attempts</th>
                  <th>Unique Connected</th>
                  <th>Contact Rate</th>
                  <th>Assigned Leads</th>
                  <th>Conversions</th>
                  <th>Talk Time</th>
                </tr>
              </thead>
              <tbody>
                {agentBreakdown.map((item) => {
                  const contactRate = item.unique_leads_attempted > 0
                    ? Math.round((item.unique_leads_reached / item.unique_leads_attempted) * 1000) / 10
                    : 0;
                  return (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td className="mono-text">{formatCompactNumber(item.total_calls)}</td>
                      <td className="mono-text">{formatCompactNumber(item.connected_calls ?? 0)}</td>
                      <td className="mono-text">{item.calls_per_day ?? 0}</td>
                      <td className="mono-text">{formatCompactNumber(item.unique_leads_attempted ?? 0)}</td>
                      <td className="mono-text">{formatCompactNumber(item.unique_leads_reached ?? 0)}</td>
                      <td className="mono-text">{contactRate}%</td>
                      <td>{formatCompactNumber(item.active_leads)}</td>
                      <td>{formatCompactNumber(item.conversions)}</td>
                      <td>{Math.round(item.average_call_duration || 0)} sec</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {agentInfoOpen ? <AgentColumnInfoModal onClose={() => setAgentInfoOpen(false)} /> : null}
        </section>
      ) : null}

      {activeReportTab === "Source Wise" ? (
        <section className="report-card">
          <div className="report-card-header">
            <div>
              <h2>Source Wise Report</h2>
              <p>Lead quality and conversion output by source.</p>
            </div>
          </div>
          <div className="source-bar-list">
            {sourceBreakdown.map((item) => (
              <SourceBarRow key={item.source_id} item={item} maxValue={maxSourceTotal} />
            ))}
          </div>
        </section>
      ) : null}

      {activeReportTab === "Status Wise" ? (
        <section className="report-card">
          <div className="report-card-header">
            <div>
              <h2>Status Wise Report</h2>
              <p>Current CRM pipeline distribution by status.</p>
            </div>
          </div>
          <div className="table-scroll report-table-scroll">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Leads</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                {(reportData?.status_breakdown || []).map((item) => (
                  <tr key={item.status}>
                    <td>{item.label || startCase(item.status)}</td>
                    <td className="mono-text">{formatCompactNumber(item.total)}</td>
                    <td>{formatRate(item.share)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeReportTab === "Region Wise" || activeReportTab === "City Wise" ? (
        <section className="report-card">
          <div className="report-card-header">
            <div>
              <h2>{activeReportTab}</h2>
              <p>
                {activeReportTab === "City Wise"
                  ? "Performance grouped by city and tagged back to the selected region."
                  : "Performance grouped by region using lead capture data."}
              </p>
            </div>
          </div>
          <div className="table-scroll report-table-scroll">
            <table className="report-table">
              <thead>
                <tr>
                  <th>{activeReportTab === "City Wise" ? "City" : "Region"}</th>
                  {activeReportTab === "City Wise" ? <th>Region</th> : null}
                  <th>Total Leads</th>
                  <th>Contacted</th>
                  <th>Converted</th>
                </tr>
              </thead>
              <tbody>
                {(activeReportTab === "City Wise" ? cityBreakdown : regionBreakdown).map((item) => (
                  <tr key={activeReportTab === "City Wise" ? `${item.city}-${item.region}` : item.region}>
                    <td>{activeReportTab === "City Wise" ? item.city : item.region}</td>
                    {activeReportTab === "City Wise" ? <td>{item.region}</td> : null}
                    <td className="mono-text">{formatCompactNumber(item.total)}</td>
                    <td>{formatCompactNumber(item.contacted)}</td>
                    <td>{formatCompactNumber(item.converted)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <DateRangeModal
        open={dateModalOpen}
        startValue={reportDraftFilters.dateFrom}
        endValue={reportDraftFilters.dateTo}
        onApply={(start, end) => {
          onReportDraftChange("dateFrom", start);
          onReportDraftChange("dateTo", end);
          setDateModalOpen(false);
        }}
        onClose={() => setDateModalOpen(false)}
      />
    </section>
  );
}

export default ReportsView;
